import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useNewsDetail } from "@/hooks/useNewsDetail";
import { LoadingScreen } from "@/components/LoadingScreen";
import { getPhotoUrl } from "@/api/client";
import { BackHeader, Card } from "@/components/kit";

interface NewsDetailPageProps {
  newsId: number;
  onBack: () => void;
}

export function NewsDetailPage({ newsId, onBack }: NewsDetailPageProps) {
  const { t } = useTranslation();
  const { news, isLoading, error } = useNewsDetail(newsId);
  const photoUrl = news ? getPhotoUrl(news.image) : null;

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
        <BackHeader title={t("myFeesViewDetail")} onBack={onBack} />
        <LoadingScreen className="min-h-[50vh]" />
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="min-h-screen" style={{ background: "var(--app-bg)" }}>
        <BackHeader title={t("myFeesViewDetail")} onBack={onBack} />
        <div className="mt-16 text-center">
          <p className="text-sm" style={{ color: "var(--c-text-muted)" }}>
            {t("errorLoading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "var(--app-bg)" }}>
      <div className="mx-auto max-w-lg">
        <BackHeader title={t("myFeesViewDetail")} onBack={onBack} />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="px-4 pt-2"
        >
          {/* Hero image */}
          <div className="overflow-hidden rounded-[20px]" style={{ background: "var(--c-line)" }}>
            {photoUrl && (
              <img src={photoUrl} alt={news.title} className="aspect-[16/10] w-full object-cover object-center" />
            )}
          </div>

          {/* Content */}
          <Card className="mt-3 p-4">
            <span className="text-xs font-medium" style={{ color: "var(--c-text-muted)" }}>
              {news.publishDate}
            </span>
            <h1 className="mt-1 text-xl font-bold leading-snug" style={{ color: "var(--c-text)" }}>
              {news.title}
            </h1>

            {news.description && (
              <div
                className="news-content mt-3 text-sm leading-relaxed"
                style={{ color: "var(--c-text-muted)" }}
                dangerouslySetInnerHTML={{ __html: news.description }}
              />
            )}

            {news.content && (
              <>
                <div className="my-4 h-px" style={{ background: "var(--c-line)" }} />
                <div className="news-content text-sm" dangerouslySetInnerHTML={{ __html: news.content }} />
              </>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
